import { useEffect, useRef, useState } from 'react';
import './App.css';
import { FIVEHEAD_ASSETS, ForeheadMeter, ForeheadMeterLab, isForeheadLabMode } from './ForeheadMeter';
import frogLogo from './frog.png';
import jupiterImage from './jupiter.webp';

const randomBetween = (min, max) => Math.random() * (max - min) + min;
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const TAU = Math.PI * 2;
const GAME_ASSETS = [
  { name: 'Fuzzy Frog logo', src: frogLogo },
  { name: 'Jupiter backdrop', src: jupiterImage },
  ...FIVEHEAD_ASSETS,
];

const readSearchNumber = (params, name, fallback, min, max) => {
  const rawValue = params.get(name);
  const value = rawValue === null ? fallback : Number(rawValue);
  if (!Number.isFinite(value)) return fallback;
  return clamp(value, min, max);
};

function loadImageAsset(asset) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    let settled = false;
    image.decoding = 'async';
    const complete = async () => {
      if (settled) return;
      settled = true;
      try {
        if (image.decode) await image.decode();
        resolve(asset);
      } catch (error) {
        reject(new Error(`${asset.name} decoded badly`));
      }
    };
    image.onload = complete;
    image.onerror = () => {
      if (settled) return;
      settled = true;
      reject(new Error(`${asset.name} failed to load`));
    };
    image.src = asset.src;
    if (image.complete && image.naturalWidth > 0) complete();
  });
}

function useAssetPreloader(assets) {
  const [loadState, setLoadState] = useState({
    complete: 0,
    failed: [],
    ready: false,
    retryKey: 0,
    total: assets.length,
  });

  useEffect(() => {
    let cancelled = false;
    const failed = [];

    setLoadState((current) => ({
      ...current,
      complete: 0,
      failed: [],
      ready: false,
      total: assets.length,
    }));

    Promise.all(
      assets.map((asset) => (
        loadImageAsset(asset)
          .catch((error) => {
            failed.push(error.message);
            throw error;
          })
          .finally(() => {
            if (!cancelled) {
              setLoadState((current) => ({
                ...current,
                complete: Math.min(current.complete + 1, assets.length),
              }));
            }
          })
      )),
    )
      .then(() => {
        if (!cancelled) {
          setLoadState((current) => ({ ...current, failed: [], ready: true }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadState((current) => ({
            ...current,
            failed: [...failed],
            ready: false,
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [assets, loadState.retryKey]);

  const retry = () => {
    setLoadState((current) => ({
      ...current,
      retryKey: current.retryKey + 1,
    }));
  };

  return {
    ...loadState,
    progress: loadState.total ? loadState.complete / loadState.total : 1,
    retry,
  };
}

const getWobbleStyle = (loop = 0) => {
  const loopBoost = Math.min(loop * 10, 80);

  return {
    '--start-rotate': `${randomBetween(-24, -10).toFixed(1)}deg`,
    '--impact-rotate-a': `${randomBetween(-3.2, 3.2).toFixed(1)}deg`,
    '--impact-rotate-b': `${randomBetween(-3.2, 3.2).toFixed(1)}deg`,
    '--impact-rotate-c': `${randomBetween(-2.8, 2.8).toFixed(1)}deg`,
    '--impact-x-a': `${randomBetween(-16, 16).toFixed(0)}px`,
    '--impact-x-b': `${randomBetween(-14, 14).toFixed(0)}px`,
    '--impact-x-c': `${randomBetween(-10, 10).toFixed(0)}px`,
    '--impact-y-a': `${randomBetween(-12, 12).toFixed(0)}px`,
    '--impact-y-b': `${randomBetween(-10, 10).toFixed(0)}px`,
    '--impact-y-c': `${randomBetween(-8, 8).toFixed(0)}px`,
    '--impact-z-a': `${(randomBetween(260, 315) + loopBoost).toFixed(0)}px`,
    '--impact-z-b': `${(randomBetween(330, 390) + loopBoost).toFixed(0)}px`,
    '--impact-z-c': `${(randomBetween(410, 480) + loopBoost).toFixed(0)}px`,
    '--impact-z-d': `${(randomBetween(720, 880) + loopBoost).toFixed(0)}px`,
    '--exit-z': `${(randomBetween(1120, 1400) + loopBoost).toFixed(0)}px`,
    '--impact-scale-a': (randomBetween(1.12, 1.24) + loopBoost / 900).toFixed(2),
    '--impact-scale-b': (randomBetween(1.24, 1.38) + loopBoost / 820).toFixed(2),
    '--impact-scale-c': (randomBetween(1.38, 1.54) + loopBoost / 760).toFixed(2),
    '--impact-scale-d': (randomBetween(1.95, 2.25) + loopBoost / 760).toFixed(2),
    '--exit-scale': (randomBetween(3.0, 3.65) + loopBoost / 620).toFixed(2),
    '--exit-rotate': `${randomBetween(10, 24).toFixed(1)}deg`,
  };
};

function wrap(value, max) {
  if (value < 0) return value + max;
  if (value > max) return value - max;
  return value;
}

function distance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function distanceSquared(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

function createAsteroidSprites(image) {
  return [1, 2, 3].reduce((sprites, size) => {
    const radius = size === 3 ? 62 : size === 2 ? 40 : 24;
    const drawSize = radius * 2.35;
    const padding = radius * 0.55;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = Math.ceil(drawSize + padding * 2);
    canvas.height = canvas.width;
    ctx.shadowBlur = size === 3 ? 22 : 16;
    ctx.shadowColor = size === 3 ? '#ad65ff' : '#b8ff4a';
    ctx.drawImage(image, padding, padding, drawSize, drawSize);
    sprites[size] = canvas;
    return sprites;
  }, {});
}

function createAsteroid(width, height, size = 3, x, y, fade = 1, difficulty = 1) {
  const edge = Math.floor(Math.random() * 4);
  const radius = size === 3 ? 62 : size === 2 ? 40 : 24;
  const moveAngle = randomBetween(0, TAU);
  const speed = randomBetween(38, 72) * difficulty;
  const spawn = {
    x: x ?? (edge === 1 ? width + radius : edge === 3 ? -radius : Math.random() * width),
    y: y ?? (edge === 0 ? -radius : edge === 2 ? height + radius : Math.random() * height),
  };

  return {
    ...spawn,
    radius,
    size,
    difficulty,
    spin: randomBetween(-1.7, 1.7) * (0.85 + difficulty * 0.2),
    angle: randomBetween(0, TAU),
    fade,
    vx: Math.cos(moveAngle) * speed,
    vy: Math.sin(moveAngle) * speed,
  };
}

function getWaveConfig(wave) {
  const count = Math.min(2 + Math.ceil(wave * 0.85), 12);
  const speedMultiplier = Math.min(0.78 + (wave - 1) * 0.08 + Math.max(0, wave - 5) * 0.045, 1.9);

  return {
    count,
    speedMultiplier,
  };
}

function createWaveAsteroids(width, height, wave) {
  const config = getWaveConfig(wave);

  return Array.from(
    { length: config.count },
    () => createAsteroid(width, height, 3, undefined, undefined, 0, config.speedMultiplier),
  );
}

function createWaveCountdown(wave) {
  return {
    index: 0,
    label: WAVE_COUNTDOWN_STEPS[0].label,
    timer: WAVE_COUNTDOWN_STEPS[0].duration,
    wave,
  };
}

function createParticle(x, y, color, options = {}) {
  const angle = options.angle ?? randomBetween(0, TAU);
  const speed = randomBetween(options.minSpeed ?? 35, options.maxSpeed ?? 220);
  return {
    x,
    y,
    vx: Math.cos(angle) * speed + (options.vx ?? 0),
    vy: Math.sin(angle) * speed + (options.vy ?? 0),
    color,
    radius: randomBetween(options.minRadius ?? 1.5, options.maxRadius ?? 5.5),
    life: randomBetween(options.minLife ?? 0.28, options.maxLife ?? 0.8),
    maxLife: 1,
  };
}

function emitParticles(game, x, y, count, colors, options = {}) {
  if (game.particles.length > MAX_PARTICLES) {
    game.particles.splice(0, game.particles.length - MAX_PARTICLES);
  }

  const available = MAX_PARTICLES - game.particles.length;
  const particleCount = Math.min(count, Math.max(0, available));

  for (let index = 0; index < particleCount; index += 1) {
    const particle = createParticle(x, y, colors[index % colors.length], options);
    particle.maxLife = particle.life;
    game.particles.push(particle);
  }
}

function drawPolygonPath(ctx, sides, radius, startAngle = -Math.PI / 2) {
  ctx.beginPath();
  for (let index = 0; index < sides; index += 1) {
    const angle = startAngle + index * (TAU / sides);
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    if (index === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

function drawPickupGlyph(ctx, pickup, pulse, progress) {
  const def = PICKUP_DEFS[pickup.type] ?? PICKUP_DEFS.SHIELD;
  const outerRadius = 26 * pulse;
  const innerRadius = 14 * pulse;

  ctx.save();
  ctx.rotate(pickup.angle);
  ctx.globalAlpha = Math.min(1, pickup.timer / 0.7);
  ctx.lineWidth = 2.4;
  ctx.strokeStyle = def.stroke;
  ctx.fillStyle = 'rgba(3, 5, 10, 0.82)';
  ctx.shadowBlur = 24;
  ctx.shadowColor = def.glow;

  if (pickup.type === 'DUAL') {
    drawPolygonPath(ctx, 4, outerRadius, Math.PI / 4);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(-12 * pulse, 0, 5 * pulse, 0, TAU);
    ctx.arc(12 * pulse, 0, 5 * pulse, 0, TAU);
    ctx.fillStyle = def.fill;
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(-18 * pulse, -10 * pulse);
    ctx.lineTo(18 * pulse, 10 * pulse);
    ctx.moveTo(-18 * pulse, 10 * pulse);
    ctx.lineTo(18 * pulse, -10 * pulse);
    ctx.strokeStyle = def.accent;
    ctx.stroke();
  } else if (pickup.type === 'HOMING') {
    drawPolygonPath(ctx, 3, outerRadius, -Math.PI / 2);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(0, 0, 21 * pulse, 10 * pulse, -0.5, 0, TAU);
    ctx.strokeStyle = def.accent;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(12 * pulse, -5 * pulse, 3.5 * pulse, 0, TAU);
    ctx.fillStyle = def.accent;
    ctx.fill();
  } else if (pickup.type === 'RAPID') {
    ctx.beginPath();
    for (let point = 0; point < 16; point += 1) {
      const angle = -Math.PI / 2 + point * (TAU / 16);
      const radius = point % 2 === 0 ? outerRadius : innerRadius;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      if (point === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(0, 0, 8 * pulse, 0, TAU);
    ctx.fillStyle = def.fill;
    ctx.fill();
  } else if (pickup.type === 'SHIELD') {
    ctx.beginPath();
    ctx.moveTo(0, -outerRadius);
    ctx.lineTo(21 * pulse, -11 * pulse);
    ctx.lineTo(16 * pulse, 14 * pulse);
    ctx.quadraticCurveTo(0, 27 * pulse, -16 * pulse, 14 * pulse);
    ctx.lineTo(-21 * pulse, -11 * pulse);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(-10 * pulse, -1 * pulse);
    ctx.lineTo(-2 * pulse, 8 * pulse);
    ctx.lineTo(12 * pulse, -10 * pulse);
    ctx.strokeStyle = def.accent;
    ctx.lineWidth = 3;
    ctx.stroke();
  }

  ctx.rotate(-pickup.angle);
  ctx.shadowBlur = 0;
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.arc(0, 0, 34 * pulse, -Math.PI / 2, -Math.PI / 2 + TAU * progress);
  ctx.stroke();
  ctx.fillStyle = '#ffffff';
  ctx.font = '900 9px Arial Black, Impact, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(def.label, 0, 0);
  ctx.restore();
}

const FIVEHEAD_START = 0.31;
const FIVEHEAD_DRAIN_RATE = 0.035;
const FIVEHEAD_HIT_REFILL = 0.035;
const FIVEHEAD_NORM_GRACE = 10;
const FIVEHEAD_FULL_BONUS_BASE = 750;
const FIVEHEAD_FULL_RESET_LEVEL = 0.9;
const FIVEHEAD_FULL_BONUS_CONSUME = 0.86;
const FULLHEAD_CHARGE_THRESHOLD = 0.985;
const PICKUP_DURATION = 7;
const WEAPON_DURATION = 6;
const RAPID_DURATION = 6;
const SHIELD_DURATION = 10;
const MAX_RAPID_STACKS = 4;
const PICKUP_TYPES = ['DUAL', 'HOMING', 'RAPID', 'SHIELD'];
const FULLHEAD_BARKS = [
  'MAXIMUM CRANIUM',
  'FOREHEAD ONLINE',
  'DOME SWEET DOME',
  'BIG BRAIN ENERGY',
  'CROWN DECK CLEAR',
  'SKULL ROOF RISING',
  'ABSOLUTE FIVEHEAD',
  'JUPITER APPROVES',
  'NOGGIN OVERDRIVE',
];
const PICKUP_DEFS = {
  DUAL: {
    label: 'DUAL',
    stroke: '#3cfff5',
    fill: 'rgba(60, 255, 245, 0.2)',
    glow: '#3cfff5',
    accent: '#b8ff4a',
  },
  HOMING: {
    label: 'HOME',
    stroke: '#b8ff4a',
    fill: 'rgba(184, 255, 74, 0.18)',
    glow: '#b8ff4a',
    accent: '#3cfff5',
  },
  RAPID: {
    label: 'RAPID',
    stroke: '#ffe86a',
    fill: 'rgba(255, 232, 106, 0.2)',
    glow: '#ffe86a',
    accent: '#ff4f9a',
  },
  SHIELD: {
    label: 'SAFE',
    stroke: '#75f6ff',
    fill: 'rgba(117, 246, 255, 0.18)',
    glow: '#75f6ff',
    accent: '#b8ff4a',
  },
};
const HUD_UPDATE_INTERVAL = 220;
const MAX_CANVAS_DPR = 1;
const MAX_GRID_DPR = 2;
const MAX_PARTICLES = 90;
const SHIP_TRAIL_INTERVAL = 0.045;
const SHIP_RESPAWN_DELAY = 1.35;
const WAVE_FROG_FADE_SPEED = 1.45;
const WAVE_BREAK_DURATION = 3.4;
const WAVE_COUNTDOWN_STEPS = [
  { label: '5', duration: 0.55 },
  { label: '4', duration: 0.48 },
  { label: '3', duration: 0.42 },
  { label: '2', duration: 0.34 },
  { label: '1', duration: 0.28 },
  { label: 'GO!', duration: 0.55 },
];
const FIVEHEAD_ENCOURAGEMENT = [
  'Forehead integrity: heroic. Ego pressure: stable.',
  'CPT. FIVEHEAD approves this unnecessary violence.',
  'Jupiter remains large, gaseous, and emotionally grateful.',
  'Excellent frog removal. Please keep the cranium inflated.',
  'That wave has been politely removed from space.',
];

const INTRO_DEFAULTS = {
  delay: 450,
  growDuration: 2100,
  anticipationDuration: 750,
  peekDuration: 950,
  pauseDuration: 1300,
  shrinkDuration: 2800,
  height: 480,
  bandHeight: 44,
  x: 50,
  top: 50,
  headRevealVisible: 26,
  width: 23,
  maxWidth: 12,
  startScale: 0.96,
  growScale: 0.76,
  peekScale: 1,
  peekVisible: 42,
  fadeAt: 28,
};

const INTRO_CONTROL_DEFS = [
  ['delay', 'Delay', 0, 2500, 50, 'ms'],
  ['growDuration', 'Grow duration', 800, 8000, 100, 'ms'],
  ['anticipationDuration', 'Anticipation', 0, 2500, 50, 'ms'],
  ['peekDuration', 'Peek duration', 200, 3000, 50, 'ms'],
  ['pauseDuration', 'Pause before drain', 0, 4000, 100, 'ms'],
  ['shrinkDuration', 'Drain duration', 500, 7000, 100, 'ms'],
  ['height', 'Head height', 360, 1100, 10, 'px'],
  ['bandHeight', 'Forehead height', 24, 96, 1, '%'],
  ['x', 'X position', 0, 100, 0.5, '%'],
  ['top', 'Hidden Y anchor', 0, 100, 0.5, '%'],
  ['headRevealVisible', 'Head reveal', 4, 42, 1, '%'],
  ['width', 'Width', 8, 42, 0.5, '%'],
  ['maxWidth', 'Max width', 4, 20, 0.25, 'rem'],
  ['startScale', 'Start scale', 0.5, 1.5, 0.01, ''],
  ['growScale', 'Grown scale', 0.5, 1.5, 0.01, ''],
  ['peekScale', 'Peek scale', 0.5, 1.5, 0.01, ''],
  ['peekVisible', 'Peek amount', 12, 72, 1, '%'],
  ['fadeAt', 'Fade in at', 0, 90, 1, '%'],
];

const DEPRECATED_INTRO_PARAMS = ['startY', 'growY', 'peekY', 'peekAt'];
const INTRO_EYE_LINE_RATIO = 0.7555;

function createShader(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }

  return shader;
}

function createProgram(gl, vertexSource, fragmentSource) {
  const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentSource);

  if (!vertexShader || !fragmentShader) return null;

  const program = gl.createProgram();
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program);
    return null;
  }

  return program;
}

function readIntroConfig() {
  const params = new URLSearchParams(window.location.search);
  return Object.fromEntries(
    INTRO_CONTROL_DEFS.map(([key, , min, max]) => [
      key,
      readSearchNumber(params, `intro${key[0].toUpperCase()}${key.slice(1)}`, INTRO_DEFAULTS[key], min, max),
    ]),
  );
}

function getMissionFiveheadPose(config, elapsedMs, dialogFrame) {
  const fadeStart = config.fadeAt / 100;
  const growPhase = clamp(elapsedMs / Math.max(1, config.growDuration), 0, 1);
  const anticipationPhase = clamp(
    (elapsedMs - config.growDuration) / Math.max(1, config.anticipationDuration),
    0,
    1,
  );
  const peekPhase = clamp(
    (elapsedMs - config.growDuration - config.anticipationDuration) / Math.max(1, config.peekDuration),
    0,
    1,
  );
  const drainPhase = clamp(
    (
      elapsedMs
      - config.growDuration
      - config.anticipationDuration
      - config.peekDuration
      - config.pauseDuration
    ) / Math.max(1, config.shrinkDuration),
    0,
    1,
  );
  const easedGrow = 1 - (1 - growPhase) ** 3;
  const anticipationBeat = Math.sin(anticipationPhase * Math.PI);
  const easedDrain = drainPhase ** 1.45;
  const easedPeek = 1 - (1 - peekPhase) ** 4;
  const growScale = config.startScale + (config.growScale - config.startScale) * easedGrow;
  const anticipationScale = growScale * (1 - anticipationBeat * 0.045);
  const scale = anticipationScale + (config.peekScale - anticipationScale) * easedPeek;
  const opacity = growPhase < fadeStart
    ? 0
    : clamp((growPhase - fadeStart) / Math.max(0.01, 1 - fadeStart), 0, 1) * 0.92;
  const anchorFrame = dialogFrame ?? {
    left: 0,
    top: 0,
    width: window.innerWidth,
    height: window.innerHeight,
  };
  const remPx = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const maxWidthPx = Math.min(config.maxWidth * remPx, config.height * 0.4267);
  const widthPx = clamp(anchorFrame.width * (config.width / 100), 56, maxWidthPx);
  const avatarHeightPx = widthPx * (2400 / 1024);
  const hiddenScale = Math.max(config.startScale, config.growScale);
  const hiddenCenterY = Math.max(
    anchorFrame.top + anchorFrame.height * (config.top / 100),
    anchorFrame.top + (avatarHeightPx * hiddenScale) / 2 + 8,
  );
  const headOnlyHeight = avatarHeightPx * config.growScale;
  const headOnlyVisiblePx = clamp(anchorFrame.height * (config.headRevealVisible / 100), 22, 86);
  const headOnlyCenterY = anchorFrame.top - headOnlyVisiblePx + headOnlyHeight / 2;
  const peekHeight = avatarHeightPx * config.peekScale;
  const eyeClearancePx = clamp(anchorFrame.height * (config.peekVisible / 100) * 0.18, 8, 44);
  const peekCenterY = anchorFrame.top
    - eyeClearancePx
    + peekHeight * (0.5 - INTRO_EYE_LINE_RATIO);
  const anticipationDipPx = anchorFrame.height * 0.045 * anticipationBeat;
  const revealOvershootPx = anchorFrame.height * 0.035 * Math.sin(peekPhase * Math.PI);
  const headRevealCenterY = hiddenCenterY + (headOnlyCenterY - hiddenCenterY) * easedGrow;
  const centerY = headRevealCenterY
    + anticipationDipPx * (1 - easedPeek)
    + (peekCenterY - headRevealCenterY) * easedPeek
    - revealOvershootPx;

  return {
    level: clamp(easedGrow * (1 - easedDrain), 0, 1),
    style: {
      left: `${anchorFrame.left + anchorFrame.width * (config.x / 100)}px`,
      opacity,
      top: `${centerY}px`,
      transform: `translate(-50%, -50%) scale(${scale})`,
      width: `${widthPx}px`,
    },
  };
}

function IntroLabControls({ config, onChange }) {
  const queryString = `?debugFps=1&introLab=1&${INTRO_CONTROL_DEFS
    .map(([key]) => `intro${key[0].toUpperCase()}${key.slice(1)}=${encodeURIComponent(config[key])}`)
    .join('&')}`;

  return (
    <form className="intro-lab-controls" aria-label="CPT FIVEHEAD intro controls">
      {INTRO_CONTROL_DEFS.map(([key, label, min, max, step, suffix]) => (
        <label key={key}>
          <span>{label} {config[key]}{suffix}</span>
          <input
            aria-label={`Intro ${label}`}
            type="range"
            min={min}
            max={max}
            step={step}
            value={config[key]}
            onChange={(event) => onChange(key, Number(event.target.value))}
            onInput={(event) => onChange(key, Number(event.target.value))}
          />
        </label>
      ))}
      <textarea
        aria-label="Current intro lab URL parameters"
        readOnly
        value={queryString}
        onFocus={(event) => event.target.select()}
      />
    </form>
  );
}

function FuzzyAsteroids({ onExit }) {
  const gridCanvasRef = useRef(null);
  const canvasRef = useRef(null);
  const shipCanvasRef = useRef(null);
  const fpsRef = useRef(null);
  const missionBriefRef = useRef(null);
  const searchParams = new URLSearchParams(window.location.search);
  const showFps = searchParams.has('debugFps');
  const showIntroLab = searchParams.has('introLab');
  const pointerRef = useRef({ active: false, x: 0, y: 0, thrust: false });
  const gridShockRef = useRef({ x: 0.5, y: 0.5, power: 0 });
  const imageRef = useRef(null);
  const asteroidSpritesRef = useRef({});
  const gameRef = useRef(null);
  const frameRef = useRef(null);
  const missionStartedRef = useRef(false);
  const [hud, setHud] = useState({
    score: 0,
    lives: 3,
    wave: 1,
    gameOver: false,
    fivehead: FIVEHEAD_START,
    fiveheadPulse: 0,
    fullheadBonus: 0,
    fullheadBark: FULLHEAD_BARKS[0],
    fullheadBonusTimer: 0,
    fullheadCharge: 0,
    activeWeapon: null,
    activeWeaponTimer: 0,
    rapidStacks: 0,
    rapidTimer: 0,
    pickup: null,
    shieldTimer: 0,
    normheadGraceLeft: FIVEHEAD_NORM_GRACE,
  });
  const [missionStarted, setMissionStarted] = useState(false);
  const [waveBreak, setWaveBreak] = useState(null);
  const [waveCountdown, setWaveCountdown] = useState(null);
  const [firstWaveStarted, setFirstWaveStarted] = useState(false);
  const [missionFiveheadLevel, setMissionFiveheadLevel] = useState(0);
  const [missionFiveheadProgress, setMissionFiveheadProgress] = useState(0);
  const [missionBriefFrame, setMissionBriefFrame] = useState(null);
  const [introConfig, setIntroConfig] = useState(readIntroConfig);
  const [restartKey, setRestartKey] = useState(0);

  useEffect(() => {
    const image = new Image();
    image.onload = () => {
      asteroidSpritesRef.current = createAsteroidSprites(image);
    };
    image.src = frogLogo;
    if (image.complete) {
      asteroidSpritesRef.current = createAsteroidSprites(image);
    }
    imageRef.current = image;
  }, []);

  useEffect(() => {
    if (missionStarted || hud.gameOver) return undefined;

    let frameId;
    let startedAt;
    const delay = introConfig.delay;
    const totalDuration = introConfig.growDuration
      + introConfig.anticipationDuration
      + introConfig.peekDuration
      + introConfig.pauseDuration
      + introConfig.shrinkDuration;

    setMissionFiveheadLevel(0);
    setMissionFiveheadProgress(0);
    const tick = (now) => {
      if (!startedAt) startedAt = now;
      const elapsed = clamp(now - startedAt - delay, 0, totalDuration);
      const pose = getMissionFiveheadPose(introConfig, elapsed, missionBriefFrame);
      setMissionFiveheadLevel(pose.level);
      setMissionFiveheadProgress(elapsed);
      if (elapsed < totalDuration) frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [introConfig, missionBriefFrame, missionStarted, hud.gameOver, restartKey]);

  useEffect(() => {
    if (missionStarted || hud.gameOver) return undefined;
    const dialog = missionBriefRef.current;
    if (!dialog) return undefined;

    let frameId = 0;
    const updateFrame = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        const rect = dialog.getBoundingClientRect();
        setMissionBriefFrame({
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        });
      });
    };

    updateFrame();
    window.addEventListener('resize', updateFrame);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateFrame);
    observer?.observe(dialog);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', updateFrame);
      observer?.disconnect();
    };
  }, [missionStarted, hud.gameOver, restartKey]);

  useEffect(() => {
    if (!showIntroLab) return;
    const params = new URLSearchParams(window.location.search);
    DEPRECATED_INTRO_PARAMS.forEach((key) => {
      params.delete(`intro${key[0].toUpperCase()}${key.slice(1)}`);
    });
    INTRO_CONTROL_DEFS.forEach(([key]) => {
      params.set(`intro${key[0].toUpperCase()}${key.slice(1)}`, String(introConfig[key]));
    });
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
  }, [introConfig, showIntroLab]);


  useEffect(() => {
    const canvas = gridCanvasRef.current;
    const gl = canvas.getContext('webgl', {
      alpha: true,
      antialias: false,
      depth: false,
      powerPreference: 'high-performance',
      stencil: false,
    });

    if (!gl) return undefined;

    const program = createProgram(
      gl,
      `
        attribute vec2 a_position;
        varying vec2 v_uv;

        void main() {
          v_uv = a_position * 0.5 + 0.5;
          gl_Position = vec4(a_position, 0.0, 1.0);
        }
      `,
      `
        precision mediump float;

        varying vec2 v_uv;
        uniform vec2 u_resolution;
        uniform vec3 u_shock;
        uniform float u_time;

        float lineMask(float coord, float spacing, float width) {
          float grid = abs(fract(coord / spacing - 0.5) - 0.5) * spacing;
          return 1.0 - smoothstep(width, width + 1.25, grid);
        }

        void main() {
          vec2 px = v_uv * u_resolution;
          vec2 center = u_resolution * 0.5;
          vec2 p = px - center;
          float dist = length(p) / max(u_resolution.x, u_resolution.y);
          float angle = atan(p.y, p.x);
          vec2 shockVector = v_uv - u_shock.xy;
          float shockDistance = length(shockVector);
          float shockWave = sin(shockDistance * 104.0 - u_time * 18.0) * exp(-shockDistance * 7.2) * u_shock.z;
          vec2 shockOffset = normalize(shockVector + 0.0001) * shockWave * 58.0;
          float wobble = sin(p.y * 0.021 + u_time * 1.25) * 5.0
            + sin(angle * 7.0 + u_time * 0.9) * 7.5 * smoothstep(0.02, 0.7, dist)
            + shockWave * 26.0;
          vec2 q = px + vec2(
            wobble,
            sin(p.x * 0.018 - u_time * 1.05) * 4.4
          ) + shockOffset;
          vec2 scrollQ = q + vec2(u_time * 58.0, -u_time * 42.0);

          float fineX = lineMask(q.x, 46.0, 1.05);
          float fineY = lineMask(q.y, 46.0, 1.05);
          float coarseX = lineMask(q.x, 184.0, 1.9);
          float coarseY = lineMask(q.y, 184.0, 1.9);
          float scrollX = lineMask(scrollQ.x, 92.0, 1.4);
          float scrollY = lineMask(scrollQ.y, 92.0, 1.4);
          float diagonal = lineMask(scrollQ.x + scrollQ.y, 220.0, 1.15);

          float fine = max(fineX, fineY);
          float coarse = max(coarseX, coarseY);
          float scroll = max(max(scrollX, scrollY) * 0.75, diagonal * 0.45);
          vec3 cyan = vec3(0.235, 1.0, 0.96);
          vec3 magenta = vec3(1.0, 0.16, 0.9);
          vec3 yellow = vec3(1.0, 0.91, 0.42);
          vec3 color = cyan * fineY * 0.55 + magenta * fineX * 0.42 + yellow * coarse * 0.36
            + mix(cyan, yellow, 0.35) * scroll * 0.38
            + vec3(1.0, 0.22, 0.95) * abs(shockWave) * 0.95;
          float alpha = clamp(fine * 0.30 + coarse * 0.22 + scroll * 0.22 + abs(shockWave) * 0.75, 0.0, 0.78);

          gl_FragColor = vec4(color, alpha);
        }
      `,
    );

    if (!program) return undefined;

    const positionLocation = gl.getAttribLocation(program, 'a_position');
    const resolutionLocation = gl.getUniformLocation(program, 'u_resolution');
    const shockLocation = gl.getUniformLocation(program, 'u_shock');
    const timeLocation = gl.getUniformLocation(program, 'u_time');
    const buffer = gl.createBuffer();

    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.useProgram(program);
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE);

    let gridFrame;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_GRID_DPR);
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    const render = (now) => {
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(program);
      const shock = gridShockRef.current;
      shock.power *= 0.94;
      if (shock.power < 0.01) shock.power = 0;
      gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      gl.uniform3f(shockLocation, shock.x, shock.y, shock.power);
      gl.uniform1f(timeLocation, now * 0.001);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      gridFrame = requestAnimationFrame(render);
    };

    resize();
    window.addEventListener('resize', resize);
    gridFrame = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(gridFrame);
      window.removeEventListener('resize', resize);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const shipCanvas = shipCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const shipCtx = shipCanvas.getContext('2d');

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_CANVAS_DPR);
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      shipCanvas.width = canvas.width;
      shipCanvas.height = canvas.height;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      shipCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

      if (!gameRef.current) return;
      gameRef.current.width = rect.width;
      gameRef.current.height = rect.height;
    };

    const getHudSnapshot = (game) => ({
      score: game.score,
      lives: game.lives,
      wave: game.wave,
      gameOver: game.gameOver,
      gameOverReason: game.gameOverReason,
      fivehead: game.fivehead,
      fiveheadPulse: game.fiveheadPulse,
      fullheadBonus: game.fullheadBonus,
      fullheadBark: game.fullheadBark,
      fullheadBonusTimer: game.fullheadBonusTimer,
      fullheadCharge: game.fullheadCharge,
      activeWeapon: game.activeWeapon,
      activeWeaponTimer: game.activeWeaponTimer,
      rapidStacks: game.rapidStacks,
      rapidTimer: game.rapidTimer,
      pickup: game.pickup,
      shieldTimer: game.shieldTimer,
      normheadGraceLeft: Math.max(0, FIVEHEAD_NORM_GRACE - game.normheadTime),
    });

    let lastHudUpdate = 0;
    const publishHud = (force = false) => {
      const game = gameRef.current;
      if (!game) return;
      const now = performance.now();
      if (!force && now - lastHudUpdate < HUD_UPDATE_INTERVAL) return;
      lastHudUpdate = now;
      setHud(getHudSnapshot(game));
    };

    const resetGame = () => {
      const rect = canvas.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;
      gameRef.current = {
        width,
        height,
        score: 0,
        lives: 3,
        wave: 1,
        gameOver: false,
        gameOverReason: '',
        fivehead: FIVEHEAD_START,
        fiveheadPulse: 0,
        fullheadBonus: 0,
        fullheadBark: FULLHEAD_BARKS[0],
        fullheadBonusTimer: 0,
        fullheadCharge: 0,
        activeWeapon: null,
        activeWeaponTimer: 0,
        rapidStacks: 0,
        rapidTimer: 0,
        pickup: null,
        shieldTimer: 0,
        fullheadArmed: true,
        normheadTime: 0,
        waveBreak: null,
        waveCountdown: null,
        waveTime: 0,
        ship: { x: width / 2, y: height / 2, vx: 0, vy: 0, angle: -Math.PI / 2, alive: true, cooldown: 0, deathPause: 0, trailCooldown: 0, invincible: 1.6 },
        bullets: [],
        particles: [],
        asteroids: createWaveAsteroids(width, height, 1),
        stars: Array.from({ length: 32 }, () => ({ x: Math.random() * width, y: Math.random() * height, r: randomBetween(0.5, 1.8), a: randomBetween(0.25, 0.85) })),
      };
      setWaveBreak(null);
      setWaveCountdown(null);
      setFirstWaveStarted(false);
      publishHud(true);
    };

    const shoot = () => {
      const game = gameRef.current;
      if (!game || game.gameOver || !game.ship.alive || game.ship.cooldown > 0) return;
      const ship = game.ship;
      const cannonOffsets = game.activeWeapon === 'DUAL' ? [-8, 8] : [0];
      cannonOffsets.forEach((offset) => {
        const sideX = Math.cos(ship.angle + Math.PI / 2) * offset;
        const sideY = Math.sin(ship.angle + Math.PI / 2) * offset;
        game.bullets.push({
          x: ship.x + Math.cos(ship.angle) * 24 + sideX,
          y: ship.y + Math.sin(ship.angle) * 24 + sideY,
          vx: ship.vx + Math.cos(ship.angle) * 520,
          vy: ship.vy + Math.sin(ship.angle) * 520,
          homing: game.activeWeapon === 'HOMING',
          life: 0.9,
        });
      });
      emitParticles(game, ship.x + Math.cos(ship.angle) * 28, ship.y + Math.sin(ship.angle) * 28, 5, ['#ffe86a', '#ffffff'], {
        angle: ship.angle,
        minSpeed: 120,
        maxSpeed: 260,
        minLife: 0.12,
        maxLife: 0.28,
        minRadius: 1,
        maxRadius: 3,
      });
      const rapidMultiplier = 1 + game.rapidStacks * 0.5;
      ship.cooldown = Math.max(0.055, 0.18 / rapidMultiplier);
    };

    const spawnPickup = (game) => {
      if (game.pickup) return;

      const type = PICKUP_TYPES[Math.floor(Math.random() * PICKUP_TYPES.length)];
      game.pickup = {
        angle: randomBetween(0, TAU),
        timer: PICKUP_DURATION,
        type,
        x: randomBetween(game.width * 0.22, game.width * 0.78),
        y: randomBetween(game.height * 0.2, game.height * 0.76),
      };
    };

    const activatePickup = (game, pickup) => {
      if (pickup.type === 'SHIELD') {
        game.shieldTimer = SHIELD_DURATION;
      } else if (pickup.type === 'RAPID') {
        game.rapidStacks = Math.min(MAX_RAPID_STACKS, game.rapidStacks + 1);
        game.rapidTimer = RAPID_DURATION;
      } else {
        game.activeWeapon = pickup.type;
        game.activeWeaponTimer = WEAPON_DURATION;
      }

      game.pickup = null;
      gridShockRef.current = {
        x: pickup.x / game.width,
        y: 1 - pickup.y / game.height,
        power: 1.2,
      };
      emitParticles(game, pickup.x, pickup.y, 34, ['#3cfff5', '#ffe86a', '#b8ff4a', '#ad65ff', '#ffffff'], {
        minSpeed: 80,
        maxSpeed: 380,
        minLife: 0.24,
        maxLife: 0.82,
        minRadius: 1.5,
        maxRadius: 6,
      });
    };

    const awardFullhead = (game) => {
      const bonus = FIVEHEAD_FULL_BONUS_BASE * game.wave;
      game.score += bonus;
      game.fullheadBonus = bonus;
      game.fullheadBark = FULLHEAD_BARKS[Math.floor(Math.random() * FULLHEAD_BARKS.length)];
      game.fullheadBonusTimer = 1.55;
      spawnPickup(game);
      game.fivehead = FIVEHEAD_FULL_BONUS_CONSUME;
      game.fullheadCharge = 0;
      game.fullheadArmed = true;
      game.fiveheadPulse = 1;
      emitParticles(game, game.width * 0.13, game.height * 0.64, 42, ['#ffe86a', '#b8ff4a', '#ad65ff', '#ffffff'], {
        minSpeed: 90,
        maxSpeed: 420,
        minLife: 0.35,
        maxLife: 1.1,
        minRadius: 2,
        maxRadius: 7,
      });
    };

    const splitAsteroid = (asteroid) => {
      const game = gameRef.current;
      game.score += asteroid.size * 100;
      game.fivehead = Math.min(1, game.fivehead + FIVEHEAD_HIT_REFILL * asteroid.size);
      game.fiveheadPulse = 0.38;
      game.normheadTime = 0;

      gridShockRef.current = {
        x: asteroid.x / game.width,
        y: 1 - asteroid.y / game.height,
        power: Math.min(1.45, gridShockRef.current.power + 0.34 + asteroid.size * 0.18),
      };
      emitParticles(game, asteroid.x, asteroid.y, asteroid.size === 1 ? 16 : 28, ['#b8ff4a', '#ad65ff', '#ffe86a', '#ffffff'], {
        vx: asteroid.vx * 0.35,
        vy: asteroid.vy * 0.35,
        minSpeed: 50,
        maxSpeed: asteroid.size === 3 ? 330 : 250,
        minLife: 0.28,
        maxLife: 0.85,
        minRadius: 1.8,
        maxRadius: asteroid.size === 3 ? 7 : 5,
      });
      if (asteroid.size === 1) return;

      game.asteroids.push(
        createAsteroid(game.width, game.height, asteroid.size - 1, asteroid.x + randomBetween(-8, 8), asteroid.y + randomBetween(-8, 8), 1, asteroid.difficulty),
        createAsteroid(game.width, game.height, asteroid.size - 1, asteroid.x + randomBetween(-8, 8), asteroid.y + randomBetween(-8, 8), 1, asteroid.difficulty),
      );
    };

    const nextWave = () => {
      const game = gameRef.current;
      game.wave += 1;
      game.waveBreak = null;
      game.waveTime = 0;
      game.asteroids = createWaveAsteroids(game.width, game.height, game.wave);
      game.waveCountdown = createWaveCountdown(game.wave);
      setWaveBreak(null);
      setWaveCountdown({ label: game.waveCountdown.label, wave: game.wave });
      publishHud(true);
    };

    const completeWave = () => {
      const game = gameRef.current;
      const summary = {
        encouragement: FIVEHEAD_ENCOURAGEMENT[(game.wave - 1) % FIVEHEAD_ENCOURAGEMENT.length],
        nextWave: game.wave + 1,
        time: game.waveTime,
        timer: WAVE_BREAK_DURATION,
        wave: game.wave,
      };
      game.waveBreak = summary;
      game.ship.vx *= 0.35;
      game.ship.vy *= 0.35;
      game.bullets = [];
      setWaveBreak(summary);
      publishHud(true);
    };

    const explodeShip = (game, isFinal = false) => {
      const ship = game.ship;
      emitParticles(
        game,
        ship.x,
        ship.y,
        isFinal ? 96 : 54,
        ['#ff2b1f', '#ff6f2a', '#ffe86a', '#ad65ff', '#b8ff4a', '#ffffff'],
        {
          minSpeed: isFinal ? 120 : 80,
          maxSpeed: isFinal ? 680 : 430,
          minLife: isFinal ? 0.45 : 0.3,
          maxLife: isFinal ? 1.45 : 0.9,
          minRadius: isFinal ? 2.5 : 1.8,
          maxRadius: isFinal ? 9 : 6.5,
        },
      );
      gridShockRef.current = {
        x: ship.x / game.width,
        y: 1 - ship.y / game.height,
        power: isFinal ? 1.55 : 1.05,
      };
    };

    const respawnShip = (game) => {
      const ship = game.ship;
      ship.x = game.width / 2;
      ship.y = game.height / 2;
      ship.vx = 0;
      ship.vy = 0;
      ship.angle = -Math.PI / 2;
      ship.alive = true;
      ship.cooldown = 0;
      ship.deathPause = 0;
      ship.trailCooldown = 0;
      ship.invincible = 1.8;
    };

    const resetFullheadStreak = (game) => {
      game.fullheadCharge = 0;
      game.activeWeapon = null;
      game.activeWeaponTimer = 0;
      game.rapidStacks = 0;
      game.rapidTimer = 0;
      game.pickup = null;
      game.shieldTimer = 0;
      game.fullheadArmed = game.fivehead < FIVEHEAD_FULL_RESET_LEVEL;
    };

    const updateShipAndWeapons = (game, ship, pointer, dt) => {
      if (pointer.active) {
        const targetAngle = Math.atan2(pointer.y - ship.y, pointer.x - ship.x);
        let delta = targetAngle - ship.angle;
        while (delta > Math.PI) delta -= TAU;
        while (delta < -Math.PI) delta += TAU;
        ship.angle += delta * Math.min(1, dt * 14);
        pointer.thrust = distance(ship, pointer) > 58;
      } else {
        pointer.thrust = false;
      }

      if (pointer.thrust) {
        ship.vx += Math.cos(ship.angle) * 340 * dt;
        ship.vy += Math.sin(ship.angle) * 340 * dt;
        shoot();
        if (ship.trailCooldown <= 0) {
          emitParticles(game, ship.x - Math.cos(ship.angle) * 18, ship.y - Math.sin(ship.angle) * 18, 3, ['#ad65ff', '#b8ff4a'], {
            angle: ship.angle + Math.PI + randomBetween(-0.4, 0.4),
            minSpeed: 40,
            maxSpeed: 130,
            minLife: 0.18,
            maxLife: 0.42,
            minRadius: 1.5,
            maxRadius: 4,
          });
          ship.trailCooldown = SHIP_TRAIL_INTERVAL;
        }
      }

      ship.vx *= 0.992;
      ship.vy *= 0.992;
      ship.x = wrap(ship.x + ship.vx * dt, game.width);
      ship.y = wrap(ship.y + ship.vy * dt, game.height);
      ship.cooldown = Math.max(0, ship.cooldown - dt);
      ship.trailCooldown = Math.max(0, ship.trailCooldown - dt);
      ship.invincible = Math.max(0, ship.invincible - dt);

      const activeBullets = [];
      game.bullets.forEach((bullet) => {
        if (bullet.homing && game.asteroids.length > 0) {
          let nearest = game.asteroids[0];
          let nearestDistance = distanceSquared(bullet, nearest);
          game.asteroids.forEach((asteroid) => {
            const candidateDistance = distanceSquared(bullet, asteroid);
            if (candidateDistance < nearestDistance) {
              nearest = asteroid;
              nearestDistance = candidateDistance;
            }
          });

          const targetAngle = Math.atan2(nearest.y - bullet.y, nearest.x - bullet.x);
          const currentSpeed = Math.max(420, Math.hypot(bullet.vx, bullet.vy));
          const desiredVx = Math.cos(targetAngle) * currentSpeed;
          const desiredVy = Math.sin(targetAngle) * currentSpeed;
          const turn = Math.min(1, dt * 4.8);
          bullet.vx += (desiredVx - bullet.vx) * turn;
          bullet.vy += (desiredVy - bullet.vy) * turn;
        }

        bullet.x = wrap(bullet.x + bullet.vx * dt, game.width);
        bullet.y = wrap(bullet.y + bullet.vy * dt, game.height);
        bullet.life -= dt;
        if (bullet.life > 0) activeBullets.push(bullet);
      });
      game.bullets = activeBullets;
    };

    const updateParticles = (game, dt) => {
      const activeParticles = [];
      game.particles.forEach((particle) => {
        particle.x = wrap(particle.x + particle.vx * dt, game.width);
        particle.y = wrap(particle.y + particle.vy * dt, game.height);
        particle.vx *= 0.985;
        particle.vy *= 0.985;
        particle.life -= dt;
        if (particle.life > 0) activeParticles.push(particle);
      });
      game.particles = activeParticles;
    };

    const update = (dt) => {
      const game = gameRef.current;
      const ship = game.ship;
      const pointer = pointerRef.current;

      if (!missionStartedRef.current) return;
      if (game.gameOver) {
        updateParticles(game, dt);
        return;
      }

      if (game.waveBreak) {
        game.waveBreak.timer -= dt;
        game.fiveheadPulse = Math.max(0, game.fiveheadPulse - dt * 0.5);
        game.fullheadBonusTimer = Math.max(0, game.fullheadBonusTimer - dt);
        if (game.pickup) {
          game.pickup.timer -= dt;
          if (game.pickup.timer <= 0) game.pickup = null;
        }
        if (game.activeWeaponTimer > 0) {
          game.activeWeaponTimer = Math.max(0, game.activeWeaponTimer - dt);
          if (game.activeWeaponTimer === 0) game.activeWeapon = null;
        }
        if (game.rapidTimer > 0) {
          game.rapidTimer = Math.max(0, game.rapidTimer - dt);
          if (game.rapidTimer === 0) game.rapidStacks = 0;
        }
        game.shieldTimer = Math.max(0, game.shieldTimer - dt);
        updateShipAndWeapons(game, ship, pointer, dt);
        updateParticles(game, dt);
        if (game.waveBreak.timer <= 0) nextWave();
        return;
      }

      if (game.waveCountdown) {
        game.waveCountdown.timer -= dt;
        updateShipAndWeapons(game, ship, pointer, dt);
        updateParticles(game, dt);
        if (game.waveCountdown.timer <= 0) {
          game.waveCountdown.index += 1;
          const nextStep = WAVE_COUNTDOWN_STEPS[game.waveCountdown.index];

          if (nextStep) {
            game.waveCountdown.label = nextStep.label;
            game.waveCountdown.timer += nextStep.duration;
            setWaveCountdown({ label: nextStep.label, wave: game.waveCountdown.wave });
            return;
          }

          game.waveCountdown = null;
          setWaveCountdown(null);
          setFirstWaveStarted(true);
          ship.invincible = Math.max(ship.invincible, 0.8);
        }
        return;
      }

      if (!ship.alive) {
        ship.deathPause = Math.max(0, ship.deathPause - dt);
        updateParticles(game, dt);
        if (ship.deathPause <= 0) {
          respawnShip(game);
          publishHud(true);
        }
        return;
      }

      game.waveTime += dt;
      game.fivehead = Math.max(0, game.fivehead - dt * FIVEHEAD_DRAIN_RATE);
      game.fiveheadPulse = Math.max(0, game.fiveheadPulse - dt);
      game.fullheadBonusTimer = Math.max(0, game.fullheadBonusTimer - dt);
      if (game.pickup) {
        game.pickup.angle += dt * 2.4;
        game.pickup.timer -= dt;
        if (game.pickup.timer <= 0) game.pickup = null;
      }
      if (game.activeWeaponTimer > 0) {
        game.activeWeaponTimer = Math.max(0, game.activeWeaponTimer - dt);
        if (game.activeWeaponTimer === 0) game.activeWeapon = null;
      }
      if (game.rapidTimer > 0) {
        game.rapidTimer = Math.max(0, game.rapidTimer - dt);
        if (game.rapidTimer === 0) game.rapidStacks = 0;
      }
      game.shieldTimer = Math.max(0, game.shieldTimer - dt);

      if (game.fullheadArmed && game.fivehead >= FULLHEAD_CHARGE_THRESHOLD) {
        awardFullhead(game);
      } else {
        game.fullheadCharge = 0;
      }

      if (game.fivehead < FIVEHEAD_FULL_RESET_LEVEL) {
        game.fullheadArmed = true;
      }

      if (game.fivehead <= 0) {
        game.normheadTime += dt;
      } else {
        game.normheadTime = 0;
      }

      if (game.normheadTime >= FIVEHEAD_NORM_GRACE) {
        explodeShip(game, true);
        ship.alive = false;
        ship.vx = 0;
        ship.vy = 0;
        game.gameOver = true;
        game.gameOverReason = 'fivehead';
        game.fivehead = 0;
        game.fiveheadPulse = 1;
        emitParticles(game, game.width * 0.13, game.height * 0.52, 36, ['#ffe86a', '#b8ff4a', '#ad65ff', '#ffffff'], {
          minSpeed: 80,
          maxSpeed: 420,
          minLife: 0.35,
          maxLife: 1.1,
          minRadius: 2,
          maxRadius: 7,
        });
        publishHud(true);
        return;
      }

      updateShipAndWeapons(game, ship, pointer, dt);

      game.asteroids.forEach((asteroid) => {
        asteroid.x = wrap(asteroid.x + asteroid.vx * dt, game.width);
        asteroid.y = wrap(asteroid.y + asteroid.vy * dt, game.height);
        asteroid.angle += asteroid.spin * dt;
        asteroid.fade = Math.min(1, asteroid.fade + dt * WAVE_FROG_FADE_SPEED);
      });

      updateParticles(game, dt);

      const remainingBullets = [];
      game.bullets.forEach((bullet) => {
        const hitIndex = game.asteroids.findIndex((asteroid) => distanceSquared(bullet, asteroid) < asteroid.radius * asteroid.radius);
        if (hitIndex === -1) {
          remainingBullets.push(bullet);
          return;
        }

        const [asteroid] = game.asteroids.splice(hitIndex, 1);
        emitParticles(game, bullet.x, bullet.y, 8, ['#ffe86a', '#ffffff'], {
          minSpeed: 40,
          maxSpeed: 180,
          minLife: 0.14,
          maxLife: 0.34,
          minRadius: 1,
          maxRadius: 3.5,
        });
        splitAsteroid(asteroid);
      });
      game.bullets = remainingBullets;

      if (game.pickup && distanceSquared(ship, game.pickup) < 46 * 46) {
        activatePickup(game, game.pickup);
        publishHud(true);
      }

      if (ship.invincible === 0) {
        const hit = game.asteroids.some((asteroid) => {
          const hitRadius = asteroid.radius + 16;
          return distanceSquared(ship, asteroid) < hitRadius * hitRadius;
        });
        if (hit) {
          if (game.shieldTimer > 0) {
            game.shieldTimer = 0;
            ship.invincible = 1.4;
            gridShockRef.current = {
              x: ship.x / game.width,
              y: 1 - ship.y / game.height,
              power: 1.25,
            };
            emitParticles(game, ship.x, ship.y, 42, ['#3cfff5', '#b8ff4a', '#ffffff'], {
              minSpeed: 80,
              maxSpeed: 420,
              minLife: 0.25,
              maxLife: 0.9,
              minRadius: 1.8,
              maxRadius: 6,
            });
            publishHud(true);
            return;
          }
          game.lives -= 1;
          resetFullheadStreak(game);
          explodeShip(game, game.lives <= 0);
          ship.alive = false;
          ship.vx = 0;
          ship.vy = 0;
          ship.cooldown = 0;
          ship.deathPause = SHIP_RESPAWN_DELAY;
          ship.trailCooldown = 0;
          if (game.lives <= 0) {
            game.gameOver = true;
            game.gameOverReason = 'lives';
            game.fivehead = 0;
            game.fiveheadPulse = 1;
            ship.deathPause = 0;
          }
          publishHud(true);
        }
      }

      if (game.asteroids.length === 0) completeWave();
      publishHud(game.gameOver);
    };

    const drawShip = (game) => {
      const ship = game.ship;
      const pointer = pointerRef.current;
      shipCtx.clearRect(0, 0, game.width, game.height);

      if (pointer.active && !game.gameOver && ship.alive) {
        const lineGradient = shipCtx.createLinearGradient(ship.x, ship.y, pointer.x, pointer.y);
        lineGradient.addColorStop(0, 'rgba(184, 255, 74, 0.82)');
        lineGradient.addColorStop(0.55, 'rgba(60, 255, 245, 0.58)');
        lineGradient.addColorStop(1, 'rgba(173, 101, 255, 0.92)');

        shipCtx.save();
        shipCtx.lineWidth = 2;
        shipCtx.strokeStyle = lineGradient;
        shipCtx.shadowBlur = 16;
        shipCtx.shadowColor = '#3cfff5';
        shipCtx.setLineDash([7, 7]);
        shipCtx.beginPath();
        shipCtx.moveTo(ship.x, ship.y);
        shipCtx.lineTo(pointer.x, pointer.y);
        shipCtx.stroke();
        shipCtx.setLineDash([]);
        shipCtx.beginPath();
        shipCtx.arc(pointer.x, pointer.y, 15, 0, TAU);
        shipCtx.stroke();
        shipCtx.beginPath();
        shipCtx.arc(pointer.x, pointer.y, 3.5, 0, TAU);
        shipCtx.fillStyle = '#ffe86a';
        shipCtx.fill();
        shipCtx.restore();
      }

      game.bullets.forEach((bullet) => {
        shipCtx.fillStyle = bullet.homing ? '#3cfff5' : '#ffe86a';
        shipCtx.shadowBlur = bullet.homing ? 12 : 0;
        shipCtx.shadowColor = '#3cfff5';
        shipCtx.beginPath();
        shipCtx.arc(bullet.x, bullet.y, bullet.homing ? 4.6 : 3.5, 0, TAU);
        shipCtx.fill();
      });
      shipCtx.shadowBlur = 0;

      if (game.gameOver || !ship.alive) return;
      if (game.shieldTimer > 0) {
        const pulse = 1 + Math.sin(performance.now() * 0.008) * 0.08;
        shipCtx.save();
        shipCtx.translate(ship.x, ship.y);
        shipCtx.scale(pulse, pulse);
        shipCtx.strokeStyle = 'rgba(117, 246, 255, 0.88)';
        shipCtx.fillStyle = 'rgba(60, 255, 245, 0.08)';
        shipCtx.shadowBlur = 24;
        shipCtx.shadowColor = '#75f6ff';
        shipCtx.lineWidth = 2.4;
        shipCtx.beginPath();
        shipCtx.arc(0, 0, 34, 0, TAU);
        shipCtx.fill();
        shipCtx.stroke();
        shipCtx.strokeStyle = 'rgba(184, 255, 74, 0.72)';
        shipCtx.lineWidth = 1.4;
        shipCtx.beginPath();
        shipCtx.arc(0, 0, 42, performance.now() * 0.004, performance.now() * 0.004 + Math.PI * 1.35);
        shipCtx.stroke();
        shipCtx.restore();
      }
      shipCtx.save();
      shipCtx.translate(ship.x, ship.y);
      shipCtx.rotate(ship.angle);
      shipCtx.globalAlpha = ship.invincible > 0 && Math.floor(performance.now() / 90) % 2 ? 0.42 : 1;
      shipCtx.strokeStyle = '#b8ff4a';
      shipCtx.fillStyle = 'rgba(6, 6, 8, 0.62)';
      shipCtx.lineWidth = 2;
      shipCtx.beginPath();
      shipCtx.moveTo(24, 0);
      shipCtx.lineTo(-16, -14);
      shipCtx.lineTo(-9, 0);
      shipCtx.lineTo(-16, 14);
      shipCtx.closePath();
      shipCtx.fill();
      shipCtx.stroke();
      shipCtx.restore();
    };

    const draw = () => {
      const game = gameRef.current;
      ctx.clearRect(0, 0, game.width, game.height);

      game.stars.forEach((star) => {
        ctx.globalAlpha = star.a;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.r, 0, TAU);
        ctx.fill();
      });
      ctx.globalAlpha = 1;

      game.asteroids.forEach((asteroid) => {
        ctx.save();
        ctx.translate(asteroid.x, asteroid.y);
        ctx.rotate(asteroid.angle);
        ctx.globalAlpha = asteroid.fade;
        const sprite = asteroidSpritesRef.current[asteroid.size];
        if (sprite) {
          const spriteSize = asteroid.radius * 3.45;
          ctx.drawImage(sprite, -spriteSize / 2, -spriteSize / 2, spriteSize, spriteSize);
        } else if (imageRef.current?.complete) {
          const size = asteroid.radius * 2.35;
          ctx.drawImage(imageRef.current, -size / 2, -size / 2, size, size);
        } else {
          ctx.fillStyle = '#ad65ff';
          ctx.beginPath();
          ctx.arc(0, 0, asteroid.radius, 0, TAU);
          ctx.fill();
        }
        ctx.restore();
      });
      ctx.globalAlpha = 1;

      if (game.pickup) {
        const pulse = 0.86 + Math.sin(performance.now() * 0.01) * 0.14;
        const timerProgress = Math.max(0, Math.min(1, game.pickup.timer / PICKUP_DURATION));
        ctx.save();
        ctx.translate(game.pickup.x, game.pickup.y);
        drawPickupGlyph(ctx, game.pickup, pulse, timerProgress);
        ctx.restore();
      }

      game.particles.forEach((particle) => {
        const alpha = Math.max(0, particle.life / particle.maxLife);
        ctx.globalAlpha = alpha;
        ctx.fillStyle = particle.color;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius * alpha, 0, TAU);
        ctx.fill();
      });
      ctx.globalAlpha = 1;

      drawShip(game);
    };

    let last = performance.now();
    let fpsLast = last;
    let fpsFrames = 0;
    const loop = (now) => {
      const dt = Math.min((now - last) / 1000, 0.033);
      last = now;
      update(dt);
      draw();
      fpsFrames += 1;
      if (now - fpsLast >= 500) {
        if (fpsRef.current) {
          fpsRef.current.textContent = `${Math.round((fpsFrames * 1000) / (now - fpsLast))} FPS`;
        }
        fpsFrames = 0;
        fpsLast = now;
      }
      frameRef.current = requestAnimationFrame(loop);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onExit();
      if (event.key.toLowerCase() === 'r' && gameRef.current?.gameOver) resetGame();
    };

    resize();
    resetGame();
    missionStartedRef.current = false;
    setMissionStarted(false);
    setWaveBreak(null);
    setWaveCountdown(null);
    setFirstWaveStarted(false);
    frameRef.current = requestAnimationFrame(loop);
    window.addEventListener('resize', resize);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onExit, restartKey]);

  const updatePointer = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    pointerRef.current.x = event.clientX - rect.left;
    pointerRef.current.y = event.clientY - rect.top;
  };
  const missionFiveheadPose = getMissionFiveheadPose(introConfig, missionFiveheadProgress, missionBriefFrame);
  const missionFiveheadIsPeeking = missionFiveheadProgress >= introConfig.growDuration + introConfig.anticipationDuration;
  const updateIntroConfig = (key, value) => {
    setIntroConfig((current) => ({ ...current, [key]: value }));
  };

  return (
    <main className="game-screen" aria-label="Fuzzy Frog asteroids game">
      <div className="game-jupiter" aria-hidden="true" />
      <div className="warp-grid" aria-hidden="true" />
      <canvas ref={gridCanvasRef} className="webgl-grid" aria-hidden="true" />
      <canvas
        ref={canvasRef}
        className="game-canvas"
        onPointerDown={(event) => {
          updatePointer(event);
          pointerRef.current.active = true;
        }}
        onPointerMove={updatePointer}
        onPointerUp={() => {
          pointerRef.current.active = false;
        }}
        onPointerLeave={() => {
          pointerRef.current.active = false;
        }}
      />
      <canvas ref={shipCanvasRef} className={`ship-canvas ${firstWaveStarted ? 'is-in-front' : ''}`.trim()} aria-hidden="true" />
      {missionStarted && !waveBreak && !waveCountdown && (
        <div className="game-hud" aria-live="polite">
          <span>Score {hud.score}</span>
          <span>Lives {hud.lives}</span>
          <span>Wave {hud.wave}</span>
          {hud.pickup && (
            <span className="hud-power">Pickup {hud.pickup.type} {Math.ceil(hud.pickup.timer)}s</span>
          )}
          {hud.activeWeapon && (
            <span className="hud-power">{hud.activeWeapon} {hud.activeWeaponTimer.toFixed(1)}s</span>
          )}
          {hud.rapidStacks > 0 && (
            <span className="hud-power">RAPID x{hud.rapidStacks} {hud.rapidTimer.toFixed(1)}s</span>
          )}
          {hud.shieldTimer > 0 && (
            <span className="hud-power">SHIELD {hud.shieldTimer.toFixed(1)}s</span>
          )}
          {hud.fivehead <= 0.001 && (
            <span className="hud-danger">Normhead {Math.ceil(hud.normheadGraceLeft)}s</span>
          )}
        </div>
      )}
      {showFps && <div ref={fpsRef} className="fps-counter" aria-label="Frame rate">-- FPS</div>}
      {missionStarted && !waveBreak && !waveCountdown && (
        <ForeheadMeter
          className={hud.gameOver ? 'is-game-over' : ''}
          level={hud.gameOver ? 0 : hud.fivehead}
          pulse={hud.gameOver ? 1 : hud.fiveheadPulse}
          won={!hud.gameOver && hud.fivehead >= 0.98}
        />
      )}
      {missionStarted && !waveBreak && !waveCountdown && !hud.gameOver && hud.fullheadBonusTimer > 0 && (
        <div className="fullhead-bonus" role="status" aria-live="polite">
          {hud.fullheadBark} +{hud.fullheadBonus}<br />
          GRAB THE PICKUP
        </div>
      )}
      <button className="exit-game" type="button" onClick={onExit}>Leave</button>
      {missionStarted && !waveBreak && !waveCountdown && !hud.gameOver && <div className="game-help">Press and drag to steer. The ship accelerates and fires toward your touch.</div>}
      {hud.gameOver && (
        <div className="game-over">
          <h2>Game Over</h2>
          <p>{hud.gameOverReason === 'fivehead' ? 'CPT. FIVEHEAD survived ten seconds of normhead. Frankly, too long.' : 'The frog field got you. Ready for another run?'}</p>
          <div className="game-over-actions">
            <button className="vector-button" type="button" onClick={() => {
              setRestartKey((key) => key + 1);
            }}>
              Play again
            </button>
            <button className="vector-button" type="button" onClick={onExit}>
              Nah Enough
            </button>
          </div>
        </div>
      )}
      {waveBreak && !hud.gameOver && (
        <div className="wave-break" role="status" aria-live="polite">
          <p className="wave-kicker">Wave {waveBreak.wave} complete</p>
          <h2>{waveBreak.time.toFixed(1)}s</h2>
          <p>{waveBreak.encouragement}</p>
          <p className="wave-next">Wave {waveBreak.nextWave} incoming</p>
        </div>
      )}
      {waveCountdown && !hud.gameOver && (
        <div className="wave-countdown" role="status" aria-live="polite">
          <p>Wave {waveCountdown.wave}</p>
          <h2>{waveCountdown.label}</h2>
        </div>
      )}
      {!missionStarted && !hud.gameOver && (
        <>
          <div className="mission-fivehead-scene" aria-hidden="true">
            <ForeheadMeter
              className={`mission-fivehead ${missionFiveheadIsPeeking ? 'is-peeking' : ''}`.trim()}
              level={missionFiveheadLevel}
              pulse={0}
              compactTuning
              danger={false}
              height={introConfig.height}
              bandHeightPct={introConfig.bandHeight}
              bandEdgeWidth={2.2}
              bandShadeTopWidth={32}
              bandShadeBottomWidth={24}
              style={missionFiveheadPose.style}
              won
            />
          </div>
          {showIntroLab && (
            <IntroLabControls config={introConfig} onChange={updateIntroConfig} />
          )}
          <div ref={missionBriefRef} className="mission-brief" role="dialog" aria-label="CPT. FIVEHEAD mission briefing">
            <p className="mission-kicker">Incoming nonsense detected</p>
            <h2>CPT. FIVEHEAD</h2>
            <div className="mission-copy">
              <p>
                Frogs. So many frogs. Why frogs? Doesn't matter. They're attacking
                Jupiter, and only one thing can stop them: your enormous,
                galaxy-grade forehead.
              </p>
              <p>
                Stay big, stay shiny, and grab FULLHEAD pickups whenever space
                starts offering suspicious gifts.
              </p>
              <p>
                Shrink down to NORMHEAD? Deeply embarrassing. You've got 10
                seconds to fix your life and regain peak cranial dominance.
              </p>
              <p>Save Jupiter. Assert forehead supremacy. No thoughts. Only forehead.</p>
            </div>
            <button
              className="vector-button"
              type="button"
              onClick={() => {
                const game = gameRef.current;
                missionStartedRef.current = true;
                setMissionStarted(true);
                if (game) {
                  game.waveCountdown = createWaveCountdown(game.wave);
                  setWaveCountdown({ label: game.waveCountdown.label, wave: game.wave });
                }
              }}
            >
              Deploy The Forehead
            </button>
          </div>
        </>
      )}
    </main>
  );
}

function SplashScreen({ onStart }) {
  const [wobbleStyle, setWobbleStyle] = useState(getWobbleStyle);
  const loopRef = useRef(0);

  const randomizeWobble = () => {
    loopRef.current += 1;
    setWobbleStyle(getWobbleStyle(loopRef.current));
  };

  return (
    <main className="App" aria-label="Fuzzy Frog placeholder">
      <div className="splash-stage">
        <img
          src={frogLogo}
          className="frog-logo"
          alt="Fuzzy Frog logo"
          onAnimationIteration={randomizeWobble}
          style={wobbleStyle}
        />
      </div>

      <div className="splash-copy">
        <h1>Guess who's back</h1>
        <p className="intro">
          Since 2009, Nottingham studio Fuzzy Frog has been creating mobile
          games, apps, and imaginative digital experiences. Having worked with
          leading brands and entertained millions, the studio is now getting
          ready to hop back into action with something new.
        </p>
        <button className="start-game" type="button" onClick={onStart}>
          What's that coming over the hill?
        </button>
      </div>
    </main>
  );
}

function LoadingScreen({ complete, failed, progress, retry, total }) {
  const percent = Math.round(progress * 100);

  return (
    <main className="App asset-loader" aria-label="Loading Fuzzy Frog assets" aria-busy={!failed.length}>
      <div className="asset-loader-panel">
        <p className="asset-loader-kicker">Priming the frog cannons</p>
        <h1>Loading</h1>
        <div className="asset-progress" aria-label={`Loading assets ${percent} percent`}>
          <span style={{ width: `${percent}%` }} />
        </div>
        <p className="asset-loader-status">
          {failed.length
            ? 'A space asset got lost in transit.'
            : `${complete} of ${total} assets ready`}
        </p>
        {failed.length > 0 && (
          <button className="vector-button" type="button" onClick={retry}>
            Retry Asset Sync
          </button>
        )}
      </div>
    </main>
  );
}

function App() {
  const [mode, setMode] = useState('splash');
  const assetLoad = useAssetPreloader(GAME_ASSETS);

  if (isForeheadLabMode()) {
    return <ForeheadMeterLab />;
  }

  if (!assetLoad.ready) {
    return <LoadingScreen {...assetLoad} />;
  }

  if (mode === 'game') {
    return <FuzzyAsteroids onExit={() => setMode('splash')} />;
  }

  return <SplashScreen onStart={() => setMode('game')} />;
}

export default App;
